import { useState, useEffect, Dispatch, SetStateAction, useContext } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  makeStyles,
  Body2,
} from "@fluentui/react-components";
import {
  AppButton,
  AppUserSelect,
  AppDateSelect,
  AppDivider,
  AppTimeInput,
  AppSelect,
  AppTextArea,
} from "../common";
import GenerateApplicationCheckbox from "./GenerateApplicationCheckbox";
import { Control, FieldValues, useForm } from "react-hook-form";
import usersService from "../../services/users.service";
import dataTimeService from "../../services/dataTime.service";
import OvertimeAssigments from "../../services/overtimeAssigments.service";
import { IUserSelectable } from "../../services/types/users";
import { applicationTypeEnum, overtimeTypeEnum, userRoleEnum } from "../../utils/enums";
import { IAssignmentsTabRow } from "../../services/types/table";
import appErrorToast from "../common/AppErrorToast";
import { IGetAssignmentsOptions } from "../../services/types/overtimeAssigments";
import UserContext from "../../modules/AppRouter/UserContext";
import { isEmploymentContract, isPayoutOnlyContract } from "../../utils/contractType";
import { getAdminCommissionPayoutRateOptions, PAYOUT_RATE_UI } from "../../utils/payoutRate";
import { useYouTrackProjectsWhenOpen } from "../../hooks/useYouTrackProjectsWhenOpen";
import {
  buildYouTrackOptionEntriesForAdd,
  formatYouTrackProjectLabel,
} from "../../utils/youtrackProject";

interface IFormData {
  employeeId: string;
  assigmentDate: Date;
  duration: string;
  type: "Wypłata" | "Do odbioru" | null;
  applicationType: "Nadgodziny" | "Wyjście" | null;
  assignmentDescription: string;
  generateApplication: boolean;
  payoutRate: string;
  youTrackProjectId?: string;
}
interface IDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  getAssignments: (options: IGetAssignmentsOptions) => void;
  setAssignments: Dispatch<React.SetStateAction<IAssignmentsTabRow[]>>;
}

const useStyles = makeStyles({
  dialogContainer: {
    maxWidth: "450px",
  },
});

const AddAssignmentDialog: React.FC<IDialogProps> = ({
  open,
  setOpen,
  getAssignments,
  setAssignments,
}) => {
  const styles = useStyles();
  const { register, handleSubmit, reset, watch, setValue, getValues, control, formState: { errors } } = useForm<IFormData>({
    defaultValues: {
      applicationType: "Nadgodziny",
      generateApplication: false,
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const { userData } = useContext(UserContext);
  const applicationTypeFieldValue = watch("applicationType");
  const isAdminUser = userData.data.baseRole === userRoleEnum.Admin;
  const needYouTrackProjectList =
    isAdminUser || applicationTypeFieldValue === "Nadgodziny";
  const youTrackProjects = useYouTrackProjectsWhenOpen(open && needYouTrackProjectList);
  const typeFieldValue = watch("type");
  const selectedEmployeeId = watch("employeeId");

  const [userOptions, setUserOptions] = useState<IUserSelectable[]>([]);

  useEffect(() => {
    usersService.selectableUsers().then((users) => {
      if (userData.data.baseRole === userRoleEnum.Employee) {
        users = users.filter(x => x.id === userData.data.id);
      }
      setUserOptions(users);
    });
  }, []);

  const selectedEmployee = userOptions.find((x) => x.id === selectedEmployeeId);
  const isPayoutOnlyEmployee =
    userData.data.baseRole === userRoleEnum.Admin
    && isPayoutOnlyContract(selectedEmployee?.contractType);

  const adminPayoutRateOptions = getAdminCommissionPayoutRateOptions(
    selectedEmployee?.contractType,
  );

  useEffect(() => {
    if (userData.data.baseRole !== userRoleEnum.Admin) {
      return;
    }
    if (isPayoutOnlyEmployee) {
      setValue("type", "Wypłata");
      setValue("payoutRate", PAYOUT_RATE_UI.R100);
      return;
    }
    const raw = getValues("payoutRate") as string | undefined;
    const unset = (raw ?? "") === "";
    const contractType = selectedEmployee?.contractType;

    if (isEmploymentContract(contractType)) {
      if (unset || raw === PAYOUT_RATE_UI.R100) {
        setValue("payoutRate", PAYOUT_RATE_UI.R150);
      }
    } else if (unset) {
      setValue("payoutRate", PAYOUT_RATE_UI.R100);
    }
  }, [
    isPayoutOnlyEmployee,
    selectedEmployeeId,
    selectedEmployee?.contractType,
    setValue,
    getValues,
    userData.data.baseRole,
  ]);

  useEffect(() => {
    if (!isAdminUser && applicationTypeFieldValue === "Wyjście") {
      setValue("youTrackProjectId", "");
    }
  }, [applicationTypeFieldValue, isAdminUser, setValue]);

  async function handleFormSubmit(data: IFormData) {
    setIsLoading(true);

    if (dataTimeService.convertTimeToMin(data.duration) < 1) {
      appErrorToast("Ustaw prawidłową liczbę nadgodzin");
      setIsLoading(false);
      return;
    }

    const isEmployeeLeaving =
      !isAdminUser && data.applicationType === "Wyjście";

    let youTrackProjectId: string | null;
    let youTrackProjectLabel: string | null;

    if (isEmployeeLeaving) {
      youTrackProjectId = null;
      youTrackProjectLabel = null;
    } else {
      const yt = youTrackProjects.find((p) => p.id === data.youTrackProjectId);
      if (!yt) {
        appErrorToast("Wybierz projekt YouTrack.");
        setIsLoading(false);
        return;
      }
      youTrackProjectId = yt.id;
      youTrackProjectLabel = formatYouTrackProjectLabel(yt.shortName, yt.name);
    }

    await OvertimeAssigments.addAssigment({
      employeeId: data.employeeId,
      assignmentDate: data.assigmentDate,
      duration: dataTimeService.convertTimeToMin(data.duration),
      type:
        data.type === "Wypłata"
          ? overtimeTypeEnum.TimeToPay
          : overtimeTypeEnum.TimetoCollection,
      applicationType:
        data.applicationType === "Wyjście"
          ? applicationTypeEnum.Leaving
          : applicationTypeEnum.Overtime,
      assignmentDescription: data.assignmentDescription,
      generateApplication: data.generateApplication,
      payoutRate: data.type === "Wypłata" ? Number(data.payoutRate) || 0 : 0,
      asAdmin: localStorage.getItem("workContext") === userRoleEnum.Admin ? true : false,
      youTrackProjectId,
      youTrackProjectLabel,
    });
    reset();
    setIsLoading(false);
    setOpen(false);
    setAssignments([]);
    getAssignments({ stopIndex: 0, reload: true });
  }
  const dialogOpenChange = () => {
    reset();
    setOpen(false);
  };
  const handleApplicationTypeChanges = () => {
    if (applicationTypeFieldValue === "Nadgodziny") {
      setValue("generateApplication", false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={dialogOpenChange}>
      <DialogSurface className={styles.dialogContainer}>
        <form>
          <DialogBody>
            <DialogTitle>{userData.data.baseRole === userRoleEnum.Admin ? "Formularz zlecenia nadgodzin" : "Wniosek o nadgodziny/wyjście"}</DialogTitle>
            <DialogContent>
              <AppDivider />
              <AppUserSelect
                label="Wybierz pracownika"
                options={userOptions}
                formHandler={register("employeeId", {
                  required: true,
                })}
              />
              <AppDivider />
              {isAdminUser && (
                <>
                  <AppSelect
                    label="Projekt (YouTrack)"
                    optionEntries={buildYouTrackOptionEntriesForAdd(youTrackProjects)}
                    formHandler={register("youTrackProjectId", { required: true })}
                  />
                  <AppDivider />
                </>
              )}
              {userData.data.baseRole === userRoleEnum.Employee && (
                <>
                  <AppSelect
                    label="Typ"
                    options={["Nadgodziny", "Wyjście"]}
                    formHandler={register("applicationType", { required: true })}
                    onClick={handleApplicationTypeChanges}
                  />
                  {applicationTypeFieldValue === "Nadgodziny" && (
                    <>
                      <AppDivider />
                      <AppSelect
                        label="Projekt (YouTrack)"
                        optionEntries={buildYouTrackOptionEntriesForAdd(youTrackProjects)}
                        formHandler={register("youTrackProjectId")}
                      />
                    </>
                  )}
                  {applicationTypeFieldValue === "Wyjście" && (
                    <>
                      <AppDivider />
                      <GenerateApplicationCheckbox
                        formHandler={register("generateApplication")}
                      />
                    </>
                  )}
                  <AppDivider />
                </>
              )}
              <AppDateSelect
                label="Wybierz datę"
                formHandler={register("assigmentDate", {
                  required: true,
                })}
                defaultValue={dataTimeService.todayString()}
              />
              <AppDivider />
              <AppTimeInput
                type="time"
                label="Liczba godzin"
                formHandler={register("duration", {
                  required: { value: true, message: "Liczba godzin jest wymagana" },
                  min: 1,
                })}
                defaultValue="01:00"
                control={control as unknown as Control<FieldValues>}
                errors={errors}
              />
              {userData.data.baseRole === userRoleEnum.Admin && (
                <>
                  <AppDivider />
                  <AppSelect
                    label="Typ"
                    options={["Wypłata", "Do odbioru"]}
                    formHandler={register("type", {
                      required: true,
                      value: "Wypłata",
                    })}
                    disabled={isPayoutOnlyEmployee}
                  />
                  {isPayoutOnlyEmployee && (
                    <>
                      <AppDivider />
                      <Body2>
                        Dla osób na umowie B2B oraz umowie zlecenie, nie ma możliwości
                        zlecenia nadgodzin do odbioru.
                      </Body2>
                    </>
                  )}
                  {typeFieldValue === "Wypłata" && (
                    <>
                      <AppDivider />
                      <AppSelect
                        label="% wynagrodzenia"
                        options={[...adminPayoutRateOptions]}
                        formHandler={register("payoutRate", {
                          required: true,
                        })}
                      />
                    </>
                  )}
                </>
              )}
              <AppDivider />
              <AppTextArea
                label="Opis"
                formHandler={register("assignmentDescription", { maxLength: 1000 })}
              />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <AppButton text="Anuluj" appearance="secondary" />
              </DialogTrigger>
              <AppButton
                text="Dodaj"
                disabled={isLoading}
                appearance="primary"
                onClick={handleSubmit((data) => {
                  handleFormSubmit(data as IFormData);
                })}
              />
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default AddAssignmentDialog;
